import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { InformationModalComponent, ModalData } from '../components/information-modal/information-modal';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  constructor(private dialog: MatDialog) { }

  /**
   * Opens an information modal with the provided configuration
   * @param data Modal configuration
   * @returns Observable that emits the action string when a button is clicked
   */
  open(data: ModalData): Observable<string> {
    const dialogRef: MatDialogRef<InformationModalComponent> = this.dialog.open(
      InformationModalComponent,
      {
        data,
        width: data.width || 'auto',
        maxWidth: '90vw',
        disableClose: false,
        autoFocus: true,
        restoreFocus: true
      }
    );

    return dialogRef.afterClosed();
  }

  /**
   * Opens a confirmation modal with Yes/No buttons
   * @param title Modal title
   * @param message Modal message
   * @param icon Optional icon
   * @returns Observable that emits 'yes' or 'no'
   */
  openConfirmation(title: string, message: string, icon?: string): Observable<string> {
    return this.open({
      title,
      message,
      icon: 'info_ic',
      buttons: [
        { text: 'Não', action: 'no', color: 'accent', style: 'outlined' },
        { text: 'Sim', action: 'yes', color: 'primary', style: 'filled' }
      ]
    });
  }

  /**
   * Opens a success modal with OK button
   * @param title Modal title
   * @param message Modal message
   * @returns Observable that emits 'ok'
   */
  openSuccess(title: string, message: string): Observable<string> {
    return this.open({
      title,
      message,
      icon: 'success_ic',
      buttons: [
        { text: 'OK', action: 'ok', color: 'primary', style: 'filled' }
      ]
    });
  }

  /**
   * Opens an error modal with OK button
   * @param title Modal title
   * @param message Modal message
   * @returns Observable that emits 'ok'
   */
  openError(title: string, message: string): Observable<string> {
    return this.open({
      title,
      message,
      icon: 'warning',
      buttons: [
        { text: 'OK', action: 'ok', color: 'warn', style: 'filled' }
      ]
    });
  }

  /**
   * Opens a warning modal with OK button
   * @param title Modal title
   * @param message Modal message
   * @returns Observable that emits 'ok'
   */
  openWarning(title: string, message: string): Observable<string> {
    return this.open({
      title,
      message,
      icon: 'warning',
      buttons: [
        { text: 'OK', action: 'ok', color: 'accent', style: 'filled' }
      ]
    });
  }

  /**
   * Opens an info modal with OK button
   * @param title Modal title
   * @param message Modal message
   * @returns Observable that emits 'ok'
   */
  openInfo(title: string, message: string): Observable<string> {
    return this.open({
      title,
      message,
      icon: 'info_ic',
      buttons: [
        { text: 'OK', action: 'ok', color: 'primary', style: 'filled' }
      ]
    });
  }

  /**
   * Opens a delete confirmation modal with single delete button and X close
   * @param title Modal title
   * @param message Modal message
   * @returns Observable that emits 'delete' or undefined (when closed with X)
   */
  openDeleteConfirmation(title: string, message: string): Observable<string> {
    return this.open({
      title,
      message,
      icon: 'info_ic',
      buttons: [
        { text: 'Remover aviso', action: 'delete', color: 'warn', style: 'filled' }
      ]
    });
  }
}